import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Pagination,
  Row,
  Select,
  Spin,
  Table,
  Tooltip,
} from 'antd';
import { FiBox, FiCheckCircle, FiChevronDown, FiLayers, FiPlus, FiRefreshCw, FiSearch, FiShare2 } from 'react-icons/fi';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import DashboardPageWrapper from '../../../components/layout/DashboardPageWrapper';
import productService from '../../../services/productService';
import realInventoryService from '../../../services/realInventoryService';
import supplierService from '../../../services/supplierService';
import inventoryPositionService from '../../../services/inventoryPositionService';
import useRemoteSelectPage from '../../../hooks/useRemoteSelectPage';
import { useAuth } from '../../../contexts/AuthContext';
import { PERMISSIONS } from '../../../config/permissions';
import CdslRealInventoryReconcileDrawer from './CdslRealInventoryReconcileDrawer';

const PRODUCTS_LIMIT = 10;
const LOTS_LIMIT = 10;
const SUPPLIER_SELECT_PAGE_SIZE = 50;

function getSupplierUuid(supplier) {
  return supplier?.uuid ?? '';
}

function apiErrMsg(err) {
  return err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Request failed';
}

function inr(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n);
}

function qty(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(n);
}

function RowSkeleton() {
  return (
    <div className="animate-pulse border border-gray-100 rounded-2xl p-4 flex items-center gap-4 bg-white">
      <div className="w-10 h-10 rounded-xl bg-gray-200 shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-3.5 bg-gray-200 rounded w-2/5" />
        <div className="h-2.5 bg-gray-200 rounded w-1/4" />
      </div>
      <div className="flex gap-3">
        <div className="w-16 h-5 bg-gray-200 rounded-full" />
        <div className="w-16 h-5 bg-gray-200 rounded-full" />
      </div>
      <div className="w-5 h-5 bg-gray-200 rounded-full" />
    </div>
  );
}

function AddRealLotForm({ productId, canEdit, onSuccess }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const selectedSupplierId = Form.useWatch('supplier_id', form);

  const fetchSuppliersPage = useCallback(async ({ page, limit, search, signal }) => {
    const { suppliers, pagination } = await supplierService.listSuppliers({
      page,
      limit,
      search,
      signal,
    });
    return {
      items: Array.isArray(suppliers) ? suppliers : [],
      pagination,
    };
  }, []);

  const {
    items: supplierItems,
    loading: suppliersLoading,
    handleSearch: handleSupplierSearch,
    handlePopupScroll: handleSupplierPopupScroll,
    handleOpenChange: handleSupplierOpenChange,
    dropdownRender: supplierDropdownRender,
    searchValue: supplierSearchValue,
  } = useRemoteSelectPage({
    enabled: formOpen && canEdit,
    fetchPage: fetchSuppliersPage,
    getKey: getSupplierUuid,
    pageSize: SUPPLIER_SELECT_PAGE_SIZE,
    selectedValue: selectedSupplierId,
    onError: (err) => toast.error(apiErrMsg(err)),
  });

  const supplierOptions = useMemo(
    () =>
      supplierItems
        .map((s) => {
          const value = getSupplierUuid(s);
          if (!value) return null;
          return {
            value,
            label: `${s.name}${s.pan ? ` (${s.pan})` : ''}`,
          };
        })
        .filter(Boolean),
    [supplierItems]
  );

  const handleFinish = async (values) => {
    const inventory_date = values.inventory_date ? dayjs(values.inventory_date).format('YYYY-MM-DD') : null;
    if (!inventory_date) {
      toast.error('Pick inventory date');
      return;
    }
    setLoading(true);
    try {
      const res = await realInventoryService.addLot(productId, {
        supplier_id: values.supplier_id,
        supplier_price: values.supplier_price,
        quantity: values.quantity,
        inventory_date,
        remarks: values.remarks?.trim() || undefined,
      });
      if (res?.success === false) {
        toast.error(res?.message || res?.error || 'Could not add real lot');
        return;
      }
      toast.success(res?.message || 'Real lot added successfully');
      form.setFieldsValue({
        supplier_id: undefined,
        supplier_price: undefined,
        quantity: undefined,
        inventory_date: dayjs(),
        remarks: '',
      });
      onSuccess?.();
    } catch (err) {
      toast.error(apiErrMsg(err));
    } finally {
      setLoading(false);
    }
  };

  if (!canEdit) {
    return (
      <p className="text-xs text-gray-400 flex items-center gap-1.5">
        <FiCheckCircle size={12} className="text-gray-300" />
        View-only — real inventory edit permission required to add real lots.
      </p>
    );
  }

  return (
    <div className="rounded-xl border border-green-100 bg-green-50/30">
      {/* toggle header */}
      <button
        type="button"
        onClick={() => setFormOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-4 py-3 hover:bg-green-50/60 transition-colors"
      >
        <div className="w-5 h-5 rounded-md bg-green-100 flex items-center justify-center shrink-0">
          <FiPlus size={11} className="text-green-600" />
        </div>
        <span className="text-xs font-semibold text-gray-700">Add Real Lot</span>
        <FiChevronDown
          size={13}
          className={`ml-auto text-gray-400 transition-transform duration-200 ${formOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* collapsible body */}
      {formOpen && (
      <div className="px-5 py-4 sm:px-6 sm:py-5 border-t border-green-100/80">
      <Form
        form={form}
        onFinish={handleFinish}
        disabled={loading}
        requiredMark={false}
        initialValues={{ inventory_date: dayjs() }}
        layout="vertical"
      >
        <Row gutter={[16, 8]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="supplier_id"
              rules={[{ required: true, message: 'Select supplier' }]}
              className="!mb-3"
              label={<span className="text-[11px] font-medium text-gray-500">Supplier</span>}
            >
              <Select
                showSearch
                loading={suppliersLoading}
                filterOption={false}
                searchValue={supplierSearchValue}
                onSearch={handleSupplierSearch}
                onPopupScroll={handleSupplierPopupScroll}
                onOpenChange={handleSupplierOpenChange}
                dropdownRender={supplierDropdownRender}
                options={supplierOptions}
                placeholder="Search supplier by name or PAN (min 3 chars)"
                className="w-full"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="supplier_price"
              rules={[{ required: true, message: 'Enter price' }]}
              className="!mb-3"
              label={<span className="text-[11px] font-medium text-gray-500">Price (₹)</span>}
            >
              <InputNumber
                min={0}
                className="w-full"
                style={{ width: '100%' }}
                placeholder="3,200"
                formatter={(v) => (v ? `₹ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '')}
                parser={(v) => v.replace(/₹\s?|(,*)/g, '')}
                controls={false}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="quantity"
              rules={[
                { required: true, message: 'Enter qty' },
                { type: 'number', min: 1, message: 'Min 1' },
              ]}
              className="!mb-3"
              label={<span className="text-[11px] font-medium text-gray-500">Qty</span>}
            >
              <InputNumber
                min={1}
                precision={0}
                className="w-full"
                style={{ width: '100%' }}
                placeholder="20"
                controls={false}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="inventory_date"
              rules={[{ required: true, message: 'Pick date' }]}
              className="!mb-3"
              label={<span className="text-[11px] font-medium text-gray-500">Inventory Date</span>}
            >
              <DatePicker className="w-full" style={{ width: '100%' }} format="DD MMM YYYY" allowClear={false} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="remarks"
              className="!mb-4"
              label={<span className="text-[11px] font-medium text-gray-500">Remarks</span>}
            >
              <Input.TextArea
                rows={3}
                maxLength={500}
                showCount
                placeholder="Optional remarks (visible on the lot record)"
                className="resize-y min-h-[72px]"
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item className="!mb-0">
              <div className="flex flex-wrap items-center gap-2">
                <Button type="primary" htmlType="submit" loading={loading} className="font-semibold">
                  {loading ? 'Adding...' : '+ Add Lot'}
                </Button>
                <Button
                  htmlType="button"
                  disabled={loading}
                  onClick={() =>
                    form.setFieldsValue({
                      supplier_id: undefined,
                      supplier_price: undefined,
                      quantity: undefined,
                      inventory_date: dayjs(),
                      remarks: '',
                    })
                  }
                >
                  Clear
                </Button>
              </div>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      </div>
      )}
    </div>
  );
}

const TX_COLUMNS = [
  { title: 'Created On', dataIndex: 'Created On', key: 'createdOn', width: 160, render: (v) => (v ? dayjs(v).format('DD MMM YYYY, HH:mm') : '—') },
  { title: 'Request ID', dataIndex: 'Request ID', key: 'requestId', width: 90 },
  { title: 'Folio ID', dataIndex: 'Folio Id', key: 'folioId', width: 120 },
  { title: 'Investor Name', dataIndex: 'Investor Name', key: 'investorName', width: 160 },
  { title: 'Qty', dataIndex: 'Qty', key: 'qty', width: 90, align: 'right', render: (v) => <span className="tabular-nums">{qty(v)}</span> },
  { title: 'Price / Range', dataIndex: 'Price / Price Range', key: 'priceRange', width: 130 },
  { title: 'Price Offered', dataIndex: 'Price Offered', key: 'priceOffered', width: 120, render: (v) => v ?? '—' },
  { title: 'Order Status', dataIndex: 'Order Status', key: 'status', width: 160 },
];

function LotsTable({ lots, txByLot, txLoadingByLot, onExpand }) {
  const columns = useMemo(
    () => [
      {
        title: 'Price',
        dataIndex: 'supplier_price',
        key: 'supplier_price',
        width: 130,
        fixed: 'left',
        render: (v) => <span className="font-semibold text-gray-900 tabular-nums">{inr(v)}</span>,
      },
      { title: 'Supplier', dataIndex: 'supplier_name', key: 'supplier_name', width: 160, ellipsis: true },
      { title: 'Inv. Date', dataIndex: 'inventory_date', key: 'inventory_date', width: 120, render: (d) => (d ? dayjs(d).format('DD MMM YYYY') : '—') },
      { title: 'Available', dataIndex: 'available_quantity', key: 'available_quantity', width: 100, align: 'right', render: (v) => <span className="font-bold text-green-600 tabular-nums">{qty(v)}</span> },
      { title: 'Original', dataIndex: 'original_quantity', key: 'original_quantity', width: 100, align: 'right', render: (v) => <span className="text-gray-700 tabular-nums">{qty(v)}</span> },
      {
        title: 'Consumed',
        key: 'consumed',
        width: 100,
        align: 'right',
        render: (_, r) => <span className="tabular-nums text-gray-500">{qty((Number(r.original_quantity) || 0) - (Number(r.available_quantity) || 0))}</span>,
      },
      { title: 'Remarks', dataIndex: 'remarks', key: 'remarks', width: 220, ellipsis: true, render: (v) => v || '—' },
      { title: 'Added By', dataIndex: 'added_by_name', key: 'added_by_name', width: 150, ellipsis: true },
      { title: 'Created', dataIndex: 'created_at', key: 'created_at', width: 130, render: (d) => (d ? dayjs(d).format('DD MMM, HH:mm') : '—') },
    ],
    []
  );

  return (
    <Table
      rowKey={(r) => r.uuid || r.id}
      columns={columns}
      dataSource={lots}
      pagination={false}
      size="small"
      scroll={{ x: 'max-content' }}
      rowClassName="hover:bg-green-50/40 transition-colors"
      expandable={{
        expandedRowRender: (record) => {
          const uuid = record.uuid;
          const loading = txLoadingByLot[uuid];
          const txData = txByLot[uuid] || [];
          if (loading) return <div className="px-1 py-2 text-xs text-gray-500">Loading transactions...</div>;
          if (txData.length === 0) return <div className="px-1 py-2 text-xs text-gray-400">No transactions found for this lot.</div>;
          return (
            <Table
              size="small"
              pagination={false}
              rowKey={(r, idx) => `${r['Request ID'] || 'req'}-${idx}`}
              columns={TX_COLUMNS}
              dataSource={txData}
              scroll={{ x: 'max-content' }}
            />
          );
        },
        onExpand,
      }}
    />
  );
}

function ShareShortfallModal({ open, product, onClose }) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [dryRunData, setDryRunData] = useState(null);
  const [configError, setConfigError] = useState('');

  useEffect(() => {
    if (!open || !product?.id) return undefined;

    let cancelled = false;
    const loadDryRun = async () => {
      setLoading(true);
      setDryRunData(null);
      setConfigError('');
      try {
        const res = await inventoryPositionService.send(product.id, { dryRun: true });
        if (cancelled) return;
        if (res?.success === false) {
          setConfigError(res?.message || 'Failed to load inventory position');
          return;
        }
        setDryRunData(res?.data ?? null);
      } catch (err) {
        if (!cancelled) {
          setConfigError(apiErrMsg(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadDryRun();
    return () => {
      cancelled = true;
    };
  }, [open, product?.id]);

  const summary = dryRunData?.summary;
  const excess = Number(summary?.totals?.excess ?? 0);
  const targetGroups = dryRunData?.target_groups ?? [];

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await inventoryPositionService.send(product.id, { dryRun: false });
      if (res?.success === false) {
        toast.error(res?.message || 'Failed to send');
        return;
      }
      const data = res?.data ?? {};
      const sent = Number(data.sent) || 0;
      const failed = Number(data.failed) || 0;
      if (failed > 0) {
        toast.error(`Sent to ${sent} group(s), ${failed} failed`);
      } else {
        toast.success(`Position summary sent to ${sent} WhatsApp group(s)`);
      }
      onClose();
    } catch (err) {
      toast.error(apiErrMsg(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      title={`Share shortfall — ${product?.name || ''}`}
      open={open}
      onCancel={onClose}
      onOk={handleSend}
      okText="Send to WhatsApp"
      okButtonProps={{
        disabled: loading || !!configError || targetGroups.length === 0,
        loading: sending,
      }}
      cancelButtonProps={{ disabled: sending }}
      width={560}
      destroyOnClose
    >
      {loading && <p className="text-sm text-gray-500">Loading position summary...</p>}

      {configError && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <p>{configError}</p>
          <Link to="/pricing/whatsapp-groups" className="text-green-700 font-medium underline mt-2 inline-block">
            Configure WhatsApp Groups
          </Link>
        </div>
      )}

      {summary && !configError && (
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg border border-gray-200 p-3">
              <div className="text-[10px] uppercase text-gray-400 font-semibold">On books</div>
              <div className="text-lg font-bold tabular-nums">{qty(summary.totals?.on_books)}</div>
            </div>
            <div className="rounded-lg border border-gray-200 p-3">
              <div className="text-[10px] uppercase text-gray-400 font-semibold">Paid orders</div>
              <div className="text-lg font-bold tabular-nums">{qty(summary.totals?.orders)}</div>
            </div>
            <div className={`rounded-lg border p-3 ${excess >= 0 ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
              <div className="text-[10px] uppercase text-gray-500 font-semibold">{excess >= 0 ? 'Excess' : 'Shortfall'}</div>
              <div className={`text-lg font-bold tabular-nums ${excess >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                {qty(Math.abs(excess))}
              </div>
            </div>
          </div>

          {summary.order_rows?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Paid orders by partner</p>
              <ul className="space-y-1 max-h-32 overflow-y-auto">
                {summary.order_rows.map((row) => (
                  <li key={row.label} className="flex justify-between text-gray-700">
                    <span>{row.label}</span>
                    <span className="tabular-nums font-medium">{qty(row.quantity)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-gray-600">
            Will send to <strong>{targetGroups.length}</strong> WhatsApp group
            {targetGroups.length === 1 ? '' : 's'}:
            {' '}
            {targetGroups.map((g) => g.group_name).join(', ') || '—'}
          </p>

          {excess < 0 && (
            <p className="text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              This product has a shortfall of {qty(Math.abs(excess))} shares against paid orders.
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

function OverallInventorySummaryModal({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [dryRunData, setDryRunData] = useState(null);
  const [configError, setConfigError] = useState('');

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    const loadDryRun = async () => {
      setLoading(true);
      setDryRunData(null);
      setConfigError('');
      try {
        const res = await inventoryPositionService.sendOverallSummary({ dryRun: true });
        if (cancelled) return;
        if (res?.success === false) {
          setConfigError(res?.message || 'Failed to load overall inventory summary');
          return;
        }
        setDryRunData(res?.data ?? null);
      } catch (err) {
        if (!cancelled) {
          setConfigError(apiErrMsg(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadDryRun();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const summary = dryRunData?.summary;
  const rows = summary?.rows ?? [];
  const targetGroups = dryRunData?.target_groups ?? [];
  const hasRows = rows.length > 0;

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await inventoryPositionService.sendOverallSummary({ dryRun: false });
      if (res?.success === false) {
        toast.error(res?.message || 'Failed to send');
        return;
      }
      const data = res?.data ?? {};
      const sent = Number(data.sent) || 0;
      const failed = Number(data.failed) || 0;
      if (failed > 0) {
        toast.error(`Sent to ${sent} group(s), ${failed} failed`);
      } else {
        toast.success(`Overall summary sent to ${sent} WhatsApp group(s)`);
      }
      onClose();
    } catch (err) {
      toast.error(apiErrMsg(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      title="Overall inventory summary"
      open={open}
      onCancel={onClose}
      onOk={handleSend}
      okText="Send to WhatsApp"
      okButtonProps={{
        disabled: loading || !!configError || !hasRows || targetGroups.length === 0,
        loading: sending,
      }}
      cancelButtonProps={{ disabled: sending }}
      width={720}
      destroyOnClose
    >
      {loading && <p className="text-sm text-gray-500">Loading overall summary...</p>}

      {configError && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <p>{configError}</p>
          <Link to="/pricing/whatsapp-groups" className="text-green-700 font-medium underline mt-2 inline-block">
            Configure WhatsApp Groups
          </Link>
        </div>
      )}

      {summary && !configError && !hasRows && (
        <p className="text-sm text-gray-600">No products with shortfall or excess today.</p>
      )}

      {summary && !configError && hasRows && (
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg border border-gray-200 p-3">
              <div className="text-[10px] uppercase text-gray-400 font-semibold">Products</div>
              <div className="text-lg font-bold tabular-nums">{summary.totals?.products ?? rows.length}</div>
            </div>
            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
              <div className="text-[10px] uppercase text-red-600 font-semibold">Shortfall</div>
              <div className="text-lg font-bold tabular-nums text-red-700">{summary.totals?.shortfall_products ?? 0}</div>
            </div>
            <div className="rounded-lg border border-green-200 bg-green-50 p-3">
              <div className="text-[10px] uppercase text-green-600 font-semibold">Excess</div>
              <div className="text-lg font-bold tabular-nums text-green-700">{summary.totals?.excess_products ?? 0}</div>
            </div>
          </div>

          <div className="overflow-x-auto max-h-64 overflow-y-auto border border-gray-100 rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-gray-500">Share</th>
                  <th className="text-right px-3 py-2 font-semibold text-gray-500">Books qty</th>
                  <th className="text-right px-3 py-2 font-semibold text-gray-500">Avg price</th>
                  <th className="text-right px-3 py-2 font-semibold text-gray-500">Paid orders</th>
                  <th className="text-right px-3 py-2 font-semibold text-gray-500">Shortfall / Excess</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const excess = Number(row.excess ?? 0);
                  return (
                    <tr key={row.share_name} className="border-t border-gray-100">
                      <td className="px-3 py-2 font-medium text-gray-800">{row.share_name}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{qty(row.books_qty)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{inr(row.books_avg_price)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{qty(row.paid_order_qty)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums font-semibold ${excess >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {excess >= 0 ? '+' : ''}{qty(excess)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="text-gray-600">
            Will send to <strong>{targetGroups.length}</strong> WhatsApp group
            {targetGroups.length === 1 ? '' : 's'}:
            {' '}
            {targetGroups.map((g) => g.group_name).join(', ') || '—'}
          </p>
        </div>
      )}
    </Modal>
  );
}

function ProductDetail({ product, canEdit }) {
  const [shortfallModalOpen, setShortfallModalOpen] = useState(false);
  const [allLots, setAllLots]         = useState([]);
  const [totalCount, setTotalCount]   = useState(0);
  const [hasMore, setHasMore]         = useState(false);
  const [nextPage, setNextPage]       = useState(2);
  const [loading, setLoading]         = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [txByLot, setTxByLot]         = useState({});
  const [txLoadingByLot, setTxLoadingByLot] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setAllLots([]);
    setHasMore(false);
    setNextPage(2);
    try {
      const lRes = await realInventoryService.getLots(product.id, { page: 1, limit: LOTS_LIMIT });
      if (lRes?.success === false) {
        toast.error(lRes?.message || 'Failed to load real lots');
      } else {
        const ld = lRes?.data ?? {};
        const fetched = Array.isArray(ld.lots) ? ld.lots : [];
        const lp = ld.pagination ?? {};
        const total = Number(lp.total) || 0;
        setAllLots(fetched);
        setTotalCount(total);
        setHasMore(fetched.length < total);
      }
    } catch (err) {
      toast.error(apiErrMsg(err));
    } finally {
      setLoading(false);
    }
  }, [product.id]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const lRes = await realInventoryService.getLots(product.id, { page: nextPage, limit: LOTS_LIMIT });
      if (lRes?.success !== false) {
        const ld = lRes?.data ?? {};
        const fetched = Array.isArray(ld.lots) ? ld.lots : [];
        setAllLots((prev) => [...prev, ...fetched]);
        setNextPage((p) => p + 1);
        const lp = ld.pagination ?? {};
        const total = Number(lp.total) || 0;
        setTotalCount(total);
        setHasMore(fetched.length > 0 && allLots.length + fetched.length < total);
      }
    } catch (err) {
      toast.error(apiErrMsg(err));
    } finally {
      setLoadingMore(false);
    }
  }, [product.id, nextPage, hasMore, loadingMore, allLots.length]);

  const { ref: sentinelRef, inView } = useInView({ rootMargin: '150px', threshold: 0 });

  useEffect(() => {
    if (inView && hasMore && !loadingMore && !loading) loadMore();
  }, [inView, hasMore, loadingMore, loading, loadMore]);

  const stats = useMemo(() => {
    const original = allLots.reduce((s, x) => s + (Number(x.original_quantity) || 0), 0);
    const available = allLots.reduce((s, x) => s + (Number(x.available_quantity) || 0), 0);
    return [
      { key: 'total_original', label: 'Total Original', value: qty(original), accent: false },
      { key: 'total_available', label: 'Available', value: qty(available), accent: true },
      { key: 'total_real_available', label: 'Real Available', value: qty(product.total_real_available), accent: true },
      { key: 'lots_count', label: 'Lots', value: String(totalCount || 0), accent: false },
    ];
  }, [allLots, totalCount, product.total_real_available]);

  const handleExpand = async (expanded, record) => {
    if (!expanded || !record.uuid || txByLot[record.uuid] || txLoadingByLot[record.uuid]) return;
    setTxLoadingByLot((p) => ({ ...p, [record.uuid]: true }));
    try {
      const res = await realInventoryService.getLotTransactions(record.uuid);
      if (res?.success === false) {
        toast.error(res?.message || 'Failed to load lot transactions');
        setTxByLot((p) => ({ ...p, [record.uuid]: [] }));
      } else {
        setTxByLot((p) => ({ ...p, [record.uuid]: Array.isArray(res?.data) ? res.data : [] }));
      }
    } catch (err) {
      toast.error(apiErrMsg(err));
      setTxByLot((p) => ({ ...p, [record.uuid]: [] }));
    } finally {
      setTxLoadingByLot((p) => ({ ...p, [record.uuid]: false }));
    }
  };

  return (
    <div className="border-t border-gray-100 bg-gray-50/40">
      {loading ? (
        <div className="px-5 py-5 text-sm text-gray-500">Loading real inventory...</div>
      ) : (
        <div className="px-5 py-5 space-y-5">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex flex-wrap gap-2 flex-1 min-w-0">
              {stats.map((s) => (
                <div
                  key={s.key}
                  className={`rounded-xl border px-4 py-3 min-w-[110px] ${s.accent ? 'bg-green-600 border-green-600 shadow-sm shadow-green-200' : 'bg-white border-gray-200 shadow-sm'}`}
                >
                  <div className={`text-[10px] uppercase tracking-wider font-semibold mb-1 ${s.accent ? 'text-green-100' : 'text-gray-400'}`}>
                    {s.label}
                  </div>
                  <div className={`text-xl font-extrabold tabular-nums ${s.accent ? 'text-white' : 'text-gray-900'}`}>{s.value}</div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 shrink-0 self-end sm:self-auto">
              {canEdit && (
                <Button
                  type="default"
                  size="small"
                  icon={<FiShare2 size={13} />}
                  onClick={() => setShortfallModalOpen(true)}
                  className="font-semibold"
                >
                  Share shortfall
                </Button>
              )}
              <button
                type="button"
                onClick={load}
                className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-white border border-gray-200 hover:border-gray-300 transition-all"
                title="Refresh"
              >
                <FiRefreshCw size={13} />
              </button>
            </div>
          </div>

          <AddRealLotForm
            productId={product.id}
            canEdit={canEdit}
            onSuccess={load}
          />

          <div>
            <div className="flex items-center gap-2 mb-3">
              <FiBox size={13} className="text-gray-400" />
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Real Inventory Lots</span>
              {totalCount > 0 && <span className="text-[11px] text-gray-400">({totalCount})</span>}
            </div>
            {allLots.length === 0 ? (
              <div className="flex flex-col items-center py-10 rounded-xl border border-dashed border-gray-200 text-center">
                <FiBox size={22} className="text-gray-300 mb-2" />
                <p className="text-sm text-gray-500 font-medium">No real lots yet</p>
              </div>
            ) : (
              <>
                <LotsTable lots={allLots} txByLot={txByLot} txLoadingByLot={txLoadingByLot} onExpand={handleExpand} />
                {hasMore && <div ref={sentinelRef} className="h-4" aria-hidden="true" />}
                {loadingMore && (
                  <div className="flex items-center justify-center gap-2 py-3">
                    <Spin size="small" />
                    <span className="text-xs text-gray-400">Loading more lots…</span>
                  </div>
                )}
                {!hasMore && allLots.length > 0 && allLots.length >= totalCount && totalCount > LOTS_LIMIT && (
                  <div className="text-center text-xs text-gray-300 py-2">All lots loaded</div>
                )}
              </>
            )}
          </div>
        </div>
      )}
      <ShareShortfallModal
        open={shortfallModalOpen}
        product={product}
        onClose={() => setShortfallModalOpen(false)}
      />
    </div>
  );
}

function ProductAccordionRow({ record, isOpen, onToggle, canEdit }) {
  const fakeCnt = Number(record.total_fake_available) || 0;
  const realCnt = Number(record.total_real_available) || 0;

  return (
    <div className={`relative rounded-2xl border overflow-hidden transition-all duration-200 ${isOpen ? 'border-green-200 shadow-md shadow-green-50' : 'border-gray-100 hover:border-gray-200 hover:shadow-sm'} bg-white`}>
      {isOpen && <span className="absolute left-0 top-0 bottom-0 w-1 bg-green-500 z-10" />}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-4 px-5 py-3.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-400 focus-visible:ring-offset-1"
      >
        <div className={`w-10 h-10 rounded-xl shrink-0 overflow-hidden flex items-center justify-center border ${isOpen ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-100'}`}>
          {record.logo_url ? <img src={record.logo_url} alt="" className="w-full h-full object-cover" /> : <span className="text-[11px] font-bold text-gray-400">{(record.name || '?').slice(0, 2).toUpperCase()}</span>}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold truncate ${isOpen ? 'text-green-800' : 'text-gray-900'}`} title={record.name}>{record.name}</p>
          <p className="text-[11px] text-gray-400 truncate mt-0.5">{record.slug || record.id}</p>
        </div>
        <div className="hidden sm:flex items-center gap-0 shrink-0 divide-x divide-gray-100">
          <Tooltip title="Fake available">
            <div className="flex flex-col items-end w-24 px-4">
              <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide leading-none mb-0.5">Fake</span>
              <span className={`text-sm font-bold tabular-nums leading-none ${fakeCnt > 0 ? 'text-green-600' : 'text-gray-300'}`}>{qty(fakeCnt)}</span>
            </div>
          </Tooltip>
          <Tooltip title="Real available">
            <div className="flex flex-col items-end w-24 px-4">
              <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide leading-none mb-0.5">Real</span>
              <span className={`text-sm font-semibold tabular-nums leading-none ${realCnt > 0 ? 'text-gray-700' : 'text-gray-300'}`}>{qty(realCnt)}</span>
            </div>
          </Tooltip>
        </div>
        <FiChevronDown size={16} className={`shrink-0 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180 text-green-600' : ''}`} />
      </button>

      {isOpen && (
        <ProductDetail
          product={record}
          canEdit={canEdit}
        />
      )}
    </div>
  );
}

export default function RealInventoryPage() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canEdit = isSuperAdmin || hasPermission(PERMISSIONS.PRODUCT_PRE_IPO_REAL_INVENTORY_EDIT);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productPage, setProductPage] = useState(1);
  const [productTotal, setProductTotal] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [openId, setOpenId] = useState(null);
  const [cdslDrawerOpen, setCdslDrawerOpen] = useState(false);
  const [overallSummaryOpen, setOverallSummaryOpen] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchDebounced(searchInput.trim());
      setProductPage(1);
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await productService.getProducts({ type: 'PRE_IPO', page: productPage, limit: PRODUCTS_LIMIT, search: searchDebounced });
      if (res?.success === false) {
        toast.error(res?.message || 'Failed to load products');
        setProducts([]);
        setProductTotal(0);
        return;
      }
      const data = res?.data ?? {};
      setProducts(Array.isArray(data.products) ? data.products : []);
      setProductTotal(Number(data.pagination?.total) || 0);
    } catch (err) {
      toast.error(apiErrMsg(err));
      setProducts([]);
      setProductTotal(0);
    } finally {
      setLoading(false);
    }
  }, [productPage, searchDebounced]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  return (
    <DashboardPageWrapper>
      <div className="max-w-[1200px] mx-auto">
        <nav className="flex items-center gap-1 text-xs text-gray-400 mb-4">
          <Link to="/dashboard" className="hover:text-gray-700 transition-colors">Dashboard</Link>
          <span className="text-gray-300">/</span>
          <Link to="/pre-ipo/listings" className="hover:text-gray-700 transition-colors">Pre-IPO</Link>
          <span className="text-gray-300">/</span>
          <span className="text-gray-600 font-medium">Real Inventory</span>
        </nav>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div>
            <h1 className="text-xl font-bold text-gray-900 tracking-tight">Real Inventory</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Manage supplier-backed real lots. Expand any lot row to see where inventory was consumed across orders.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 shrink-0">
            {canEdit && (
              <>
                <Button
                  type="default"
                  icon={<FiShare2 size={13} />}
                  onClick={() => setOverallSummaryOpen(true)}
                  className="font-semibold"
                >
                  Overall inventory summary
                </Button>
                <Button type="primary" onClick={() => setCdslDrawerOpen(true)}>
                  Reconcile credit statement
                </Button>
              </>
            )}
            <Button icon={<FiRefreshCw size={13} />} onClick={fetchProducts} loading={loading} className="hidden sm:flex items-center gap-1.5">
              Refresh
            </Button>
          </div>
        </div>

        <div className="mb-4 flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <FiSearch size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <Input allowClear placeholder="Search products..." value={searchInput} onChange={(e) => setSearchInput(e.target.value)} style={{ paddingLeft: 32 }} size="middle" />
          </div>
          {!loading && <span className="text-xs text-gray-400 whitespace-nowrap">{productTotal} products</span>}
        </div>

        {loading ? (
          <div className="space-y-3">{Array.from({ length: 6 }).map((_, i) => <RowSkeleton key={i} />)}</div>
        ) : products.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 rounded-2xl border border-dashed border-gray-200 bg-white text-center">
            <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center mb-4">
              <FiLayers size={22} className="text-gray-400" />
            </div>
            <p className="font-semibold text-gray-700">No products found</p>
            <p className="text-sm text-gray-400 mt-1">Try a different search term.</p>
          </div>
        ) : (
          <div className="space-y-3 relative">
            {products.map((p) => (
              <ProductAccordionRow
                key={p.id}
                record={p}
                isOpen={openId === p.id}
                onToggle={() => setOpenId((prev) => (prev === p.id ? null : p.id))}
                canEdit={canEdit}
              />
            ))}
          </div>
        )}

        {!loading && productTotal > PRODUCTS_LIMIT && (
          <div className="flex justify-center mt-6 pt-4 border-t border-gray-100">
            <Pagination
              current={productPage}
              pageSize={PRODUCTS_LIMIT}
              total={productTotal}
              showSizeChanger={false}
              onChange={(p) => { setProductPage(p); setOpenId(null); }}
              showTotal={(t) => <span className="text-xs text-gray-500">{t} products</span>}
            />
          </div>
        )}

        <CdslRealInventoryReconcileDrawer
          open={cdslDrawerOpen}
          onClose={() => setCdslDrawerOpen(false)}
          onSuccess={fetchProducts}
        />

        <OverallInventorySummaryModal
          open={overallSummaryOpen}
          onClose={() => setOverallSummaryOpen(false)}
        />
      </div>
    </DashboardPageWrapper>
  );
}
